// src/Components/ImportMarkers/ExcelDialogDescContent.js

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import DialogActionBar from './DialogActionBar';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

export default function ExcelDialogDescContent({
  isOpen,
  isTypeDesc,
  makeUppercase,
  colName,
  descSearchQuery,
  setDescSearchQuery,
  handleFetchDescSingle,
  isFetchingDesc,
  isBulkGenerating,
  descSearchError,
  descText,
  setDescText,
  handleCopyText,
  overwritePrompt,
  setOverwritePrompt,
  handleDescSubmit,
  applyToAllRows,
  setApplyToAllRows,
  handleCancelClick,
  handleNavUp,
  handleNavDown,
  handleNavPrev,
  handleNavNext,
  isDescOkDisabled,
  initiateBulkProcess,
  isOkPressed,
}) {
  if (!isOpen || !isTypeDesc) return null;

  return (
    <Box
      id='excel-desc-panel'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        py: 2,
        pl: 3,
        pr: '52px',
      }}>
      <Typography
        variant='body2'
        sx={{ fontWeight: 'bold', mb: 2, color: 'text.secondary' }}>
        Assign Description:{' '}
        {makeUppercase && typeof colName === 'string' ? colName.toUpperCase() : colName}
      </Typography>

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minHeight: 0, // Critical for Flexbox nested scrolling
        }}>
        <Box sx={{ width: '100%', pb: 0, pt: 0, flexShrink: 0 }}>
          <FormControl
            variant='outlined'
            fullWidth
            sx={{ height: '36px', justifyContent: 'center' }}>
            <OutlinedInput
              id='desc-search-input'
              placeholder='Search for AI Description...'
              value={descSearchQuery}
              onChange={(e) => setDescSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleFetchDescSingle();
              }}
              disabled={isFetchingDesc || isBulkGenerating}
              sx={{
                borderRadius: '50px',
                height: '36px',
                backgroundColor:
                  isFetchingDesc || isBulkGenerating ? 'rgba(0,0,0,0.02)' : 'white',
                fontSize: '0.875rem',
                transition: 'all 0.3s ease',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.2)',
                  borderWidth: '1px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor:
                    isFetchingDesc || isBulkGenerating
                      ? 'rgba(0,0,0,0.2)'
                      : 'rgba(0,0,0,0.4)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0055ff',
                  borderWidth: '2px',
                },
                '& .MuiInputBase-input': {
                  textTransform: makeUppercase ? 'uppercase' : 'none',
                },
              }}
              startAdornment={
                <InputAdornment position='start' sx={{ pl: 0.5 }}>
                  {isFetchingDesc && !isBulkGenerating ? (
                    <CircularProgress size={18} thickness={5} />
                  ) : (
                    <IconButton
                      id='desc-search-button'
                      size='small'
                      onClick={handleFetchDescSingle}
                      disabled={
                        isFetchingDesc || isBulkGenerating || !descSearchQuery.trim()
                      }
                      sx={{
                        padding: '2px',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform:
                            isFetchingDesc || isBulkGenerating ? 'none' : 'scale(1.15)',
                          backgroundColor: 'transparent',
                          color:
                            isFetchingDesc || isBulkGenerating ? 'inherit' : '#0055ff',
                        },
                      }}>
                      <SearchIcon fontSize='small' />
                    </IconButton>
                  )}
                </InputAdornment>
              }
              endAdornment={
                descSearchQuery && (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setDescSearchQuery('')}
                      size='small'
                      disabled={isFetchingDesc || isBulkGenerating}
                      sx={{
                        p: '2px',
                        '&:hover': {
                          color:
                            isFetchingDesc || isBulkGenerating ? 'inherit' : '#ff1744',
                        },
                      }}>
                      <CancelIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                )
              }
            />
          </FormControl>
        </Box>

        {descSearchError && (
          <Typography color='error' variant='body2' sx={{ px: 1, flexShrink: 0 }}>
            {descSearchError}
          </Typography>
        )}

        <Box sx={{ position: 'relative', display: 'flex', flexGrow: 1, minHeight: 0 }}>
          <TextField
            multiline
            fullWidth
            variant='outlined'
            value={descText}
            onChange={(e) => setDescText(e.target.value)}
            disabled={isFetchingDesc || isBulkGenerating}
            placeholder='AI generated descriptions will appear here...'
            sx={{
              backgroundColor: '#fff',
              flexGrow: 1,
              minHeight: 0, // Prevents flex blowout
              display: 'flex',
              flexDirection: 'column',
              '& .MuiInputBase-root': {
                height: '100%',
                alignItems: 'flex-start',
                padding: 0, // Remove root padding to maximize click area
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                lineHeight: 1.5,
                height: '100% !important',
                overflowY: 'auto !important',
                padding: '12px', // Apply padding directly to textarea
                boxSizing: 'border-box',
                textTransform: 'none !important', // AMENDED: Prevent global theme from capitalizing
              },
            }}
          />
          <Tooltip title='Copy Text' placement='top'>
            <span>
              <IconButton
                onClick={handleCopyText}
                disabled={!descText || isFetchingDesc || isBulkGenerating}
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: -40, // Places the icon completely outside the text box in the parent's padding
                  color: 'text.secondary',
                  padding: '4px',
                  '&:hover': { color: '#1976d2', backgroundColor: 'transparent' },
                }}>
                <ContentCopyIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <ConfirmDialog
        open={overwritePrompt.open}
        onClose={() =>
          setOverwritePrompt({
            open: false,
            filledCount: 0,
            totalCount: 0,
            goToNext: false,
          })
        }
        onConfirm={() => {
          const goToNext = overwritePrompt.goToNext;
          setOverwritePrompt({
            open: false,
            filledCount: 0,
            totalCount: 0,
            goToNext: false,
          });
          handleDescSubmit(goToNext, true);
        }}
        message={`There are descriptions in ${overwritePrompt.filledCount} of ${overwritePrompt.totalCount} rows. Do you want to overwrite them?`}
        severity='warning'
        confirmText='OK'
        cancelText='Cancel'
        sx={{ zIndex: 100005 }}
      />

      <DialogActionBar
        applyToAllChecked={applyToAllRows}
        onApplyToAllChange={(e) => setApplyToAllRows(e.target.checked)}
        disableApplyToAll={isFetchingDesc || isBulkGenerating}
        onCancel={handleCancelClick}
        disableCancel={isFetchingDesc && !isBulkGenerating}
        onUp={handleNavUp}
        onDown={handleNavDown}
        onPrev={handleNavPrev}
        onNext={handleNavNext}
        disableNav={isDescOkDisabled}
        onOk={() => {
          if (applyToAllRows) initiateBulkProcess(false);
          else handleDescSubmit(false, false);
        }}
        disableOk={isDescOkDisabled}
        isOkPressed={isOkPressed}
        isLoadingOk={isBulkGenerating && applyToAllRows}
      />
    </Box>
  );
}
